/**
 * Section-switch reason document schema + paths.
 *
 * When a student switches sections from the section-switch screen
 * (`webapp/src/course/sectionswitch/SectionSwitch.tsx`) they say *why*
 * before submitting, and the answer is recorded once the switch succeeds.
 * The captured doc lives under `sectionSwitchReasons/{courseId}/reasons/{reasonId}`.
 *
 * The `{courseId}` segment is in the path so the security rule can call
 * `isInCourse(courseId)` directly without an extra `get()`, mirroring the
 * `sectionAnalysis` convention. See the rule block in
 * `src/tooling/firebase-config/firestore/default/firestore.rules`.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../types';

/**
 * Closed list of reason categories surfaced to the student as selectable
 * chips. Keep in sync with the chip labels rendered in the section-switch
 * UI.
 *
 * - `schedule`            — schedule conflict
 * - `section_leader`      — issue with section leader
 * - `peers`               — issue with classmates
 * - `too_small`           — section feels empty
 * - `too_crowded`         — section feels too full
 * - `timezone_confusion`  — section time was confusing across timezones
 * - `other`               — anything else; details in `reasonText`
 */
export const SECTION_SWITCH_REASON_CATEGORIES = [
  'schedule',
  'section_leader',
  'peers',
  'too_small',
  'too_crowded',
  'timezone_confusion',
  'other',
] as const;

/** Zod enum for {@link SECTION_SWITCH_REASON_CATEGORIES}. */
export const SectionSwitchReasonCategorySchema = z.enum(
  SECTION_SWITCH_REASON_CATEGORIES,
);

/** Reason-category union derived from {@link SectionSwitchReasonCategorySchema}. */
export type SectionSwitchReasonCategory = z.infer<
  typeof SectionSwitchReasonCategorySchema
>;

/**
 * Stored shape for a `sectionSwitchReasons/{courseId}/reasons/{reasonId}`
 * doc. Written client-side from the section-switch flow after a
 * successful switch via fire-and-forget `addDoc` — failures must not
 * surface to the user since the switch itself has already completed.
 */
export const SectionSwitchReasonDocSchema = z
  .object({
    /** The student who initiated the switch. Equals `request.auth.uid` per the security rule. */
    userId: z.string(),
    /** The course the switch happened in. Redundant with the path; carried for collection-group queries. */
    courseId: z.string(),
    /** Section the student left. */
    previousSectionId: z.string(),
    /**
     * User id of the SL recorded on `previousSectionId.sectionLeader`
     * at the time of the switch — i.e. the *originally assigned* SL
     * for that section.
     *
     * Not comprehensive around permanent section covers: when a
     * different SL has been covering the section every week via the
     * `covers` mechanism, the section doc's `sectionLeader` can be
     * stale and not reflect who actually teaches the section
     * regularly. Pair with {@link lastAttendingSectionLeaderId} for
     * the cover-aware "who the student actually experienced" signal.
     *
     * `null` when the section has no SL assigned.
     */
    previousAssignedSectionLeaderId: z.string().nullable(),
    /**
     * Best-effort user id of the SL who taught the student's most
     * recent attended meeting **in whichever section they were in
     * that week** — not necessarily `previousSectionId`. When the
     * student has done prior section switches, the last attended
     * section can be one they left earlier; the writer resolves the
     * section id from the user's `sectionAttendanceIds` doc (a per-
     * week list of attended section ids), not from
     * `previousSectionId`. Once that section is resolved, the SL is
     * derived as:
     *
     *   1. If `sectionLeaderRSVP[week] === true`, the primary
     *      `sectionLeader` — a confirmed RSVP supersedes any
     *      lingering cover entry (see issue #2814: covers are not
     *      cleared when the primary belatedly RSVPs yes).
     *   2. Else if `covers[week]` exists, the cover SL.
     *   3. Else the primary `sectionLeader`.
     *
     * Known limitations (data, not code): we do *not* know whether
     * the primary SL actually showed despite an RSVP, and we do not
     * know whether an unrecorded cover stepped in informally. A
     * specific edge case worth calling out: when the primary did not
     * RSVP, a cover signed up, and the primary then showed up anyway
     * (cover may or may not have come), this field will record the
     * cover — `teacherAttendance[week]` is a boolean and flips true
     * for any authorized teacher, so the section doc alone cannot
     * disambiguate which one taught. Tracked at issue #2899 (add a
     * who-actually-taught signal at attendance write time). Treat
     * this field as a strong hint rather than ground truth, and
     * cross-reference attendance/Zoom signals in analyses where
     * precision matters.
     *
     * `null` when the student had no recorded section attendance, or
     * when neither a cover nor a primary SL resolves for the
     * attended week on the resolved section doc.
     */
    lastAttendingSectionLeaderId: z.string().nullable(),
    /** Section the student joined. */
    newSectionId: z.string(),
    /** `timeIndex` of the previous section (timezone-fixed offset; see `SectionDocument`). */
    previousTimeIndex: z.number(),
    /** `timeIndex` of the new section. */
    newTimeIndex: z.number(),
    /** Whether the student is a minor at switch time (mirrors `UserData.isMinor`). */
    isMinor: z.boolean(),
    /**
     * 1-based section week in which the switch occurred (1 = the
     * first section week of the course). Comes directly from
     * `getCurrentSectionWeek` (see `@cip/core/section/courseWeekApi`),
     * clamped at the call site so pre-course observations record `1`.
     *
     * Boundary convention (from `getCurrentSectionWeek`): the moment
     * the *last* section of week N starts is treated as the
     * transition into week N+1, on the rationale that all of week N's
     * sections are now in the past. So a switch 1 second after week
     * 1's last section starts records `2`, not `1`.
     */
    courseSectionWeekAtSwitch: z.number().int().min(1),
    /**
     * Number of section meetings this student had attended before
     * switching, derived from the per-user attendance doc at
     * `users/{userId}/{courseId}/sectionAttendance`. Defaults to 0 when
     * the attendance doc is missing or unreadable.
     */
    sectionsAttendedBeforeSwitch: z.number().int().nonnegative(),
    /** Selected reason category. See {@link SECTION_SWITCH_REASON_CATEGORIES}. */
    reasonCategory: SectionSwitchReasonCategorySchema,
    /** Free-text elaboration, trimmed by the writer. Empty when the student left it blank. */
    reasonText: z.string(),
    /** Server-assigned write time (`serverTimestamp()` at write). */
    createdAt: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link SectionSwitchReasonDocSchema}. */
export type SectionSwitchReasonDoc = z.infer<
  typeof SectionSwitchReasonDocSchema
>;

// ============================================================================
// Paths
// ============================================================================

/** Root collection name. The first doc segment is `{courseId}`. */
export const SECTION_SWITCH_REASONS_COLLECTION = 'sectionSwitchReasons';

/** Leaf collection name under the course-scoped parent doc. */
export const SECTION_SWITCH_REASONS_LEAF_COLLECTION = 'reasons';

/**
 * Firestore collection path for the reasons of a particular course.
 *
 * @param args.courseId - The course id.
 * @returns `sectionSwitchReasons/{courseId}/reasons`.
 */
export function getSectionSwitchReasonsCollectionPath(args: {
  courseId: string;
}): string {
  return `${SECTION_SWITCH_REASONS_COLLECTION}/${args.courseId}/${SECTION_SWITCH_REASONS_LEAF_COLLECTION}`;
}

/**
 * Firestore doc path for a single reason.
 *
 * @param args.courseId - The course id.
 * @param args.reasonId - The reason doc id.
 * @returns `sectionSwitchReasons/{courseId}/reasons/{reasonId}`.
 */
export function getSectionSwitchReasonDocPath(args: {
  courseId: string;
  reasonId: string;
}): string {
  return `${getSectionSwitchReasonsCollectionPath(args)}/${args.reasonId}`;
}
